import { useState, useEffect, useMemo } from "react";
import {
  FileText,
  DollarSign,
  Clock,
  Filter,
  Search,
  X,
  Sun,
  Moon,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
} from "lucide-react";
import {
  getDadosControleCompetencia,
  getDadosBacklog2026,
  getDadosBacklog2025,
  getDadosNfe,
} from "../api";
import SideBarNavDash from "../componets/SideBarNavDash";
import SideBarNavFaturamento from "../componets/SideBarNavFaturamento";
import { useNavigate , useLocation } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

// ─── Tema ────────────────────────────────────────────────────────────────────
const THEMES = {
  dark: {
    bg: "#0f0f13",
    card: "#1a1a22",
    border: "#2a2a38",
    text: "#e8e8f0",
    muted: "#8888a8",
    brand: "#ec3204ff",
    input: "#12121a",
    hover: "#22222e",
  },
  light: {
    bg: "#f4f4f8",
    card: "#ffffff",
    border: "#e0e0ec",
    text: "#1a1a2e",
    muted: "#6b6b8a",
    brand: "#f11109ff",
    input: "#f9f9fc",
    hover: "#f0f0f8",
  },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmtBRL = (v) => {
  const n =
    typeof v === "string"
      ? parseFloat(v.replace(/[^\d.,-]/g, "").replace(",", "."))
      : v;
  if (isNaN(n)) return "—";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(n);
};

const parseDate = (str) => {
  if (!str) return null;

  // Suporta MM/YYYY (Comum em competência) -> transforma em YYYY-MM-01
  if (/^\d{2}\/\d{4}$/.test(str)) {
    const [m, y] = str.split("/");
    return new Date(parseInt(y), parseInt(m) - 1, 1);
  }

  // Suporta dd/mm/yyyy
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) {
    const [d, m, y] = str.split("/");
    return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
  }

  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
};

const fmtDate = (str) => {
  const d = parseDate(str);
  if (!d) return "—";
  return d.toLocaleDateString("pt-BR");
};

// Normaliza datas para o padrão MM/YYYY
const padronizarMesAno = (dataStr) => {
  if (!dataStr) return "";
  const str = dataStr.toString().trim();

  // Formato DD/MM/YYYY (ex: "01/01/2026" -> "01/2026")
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) {
    const [, mes, ano] = str.split("/");
    return `${mes}/${ano}`;
  }

  // Formato M/YYYY ou MM/YYYY (ex: "5/2025" -> "05/2025")
  if (/^\d{1,2}\/\d{4}$/.test(str)) {
    const [mes, ano] = str.split("/");
    return `${mes.padStart(2, "0")}/${ano}`;
  }

  // Formato ISO YYYY-MM-DD (ex: "2026-01-01" -> "01/2026")
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
    const [ano, mes] = str.split("-");
    return `${mes}/${ano}`;
  }

  return norm(str); // Fallback
};

// Normaliza string para comparação de join
const norm = (s) => (s ?? "").toString().trim().toLowerCase();

// Cálculo do Aging (Data Atual - Último dia da Competência)
const calcularAging = (compStr) => {
  if (!compStr) return null;
  const str = padronizarMesAno(compStr);
  if (!str || !str.includes("/")) return null;

  const [m, y] = str.split("/");
  // new Date(ano, mes, 0) retorna o último dia do mês anterior ao passado.
  // Como 'm' já vem como índice normalizado de mês 1-12, isso retornará o último dia do mês exato 'm'.
  const lastDay = new Date(parseInt(y, 10), parseInt(m, 10), 0);

  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  lastDay.setHours(0, 0, 0, 0);

  // Diferença em dias
  return Math.floor(
    (hoje.getTime() - lastDay.getTime()) / (1000 * 60 * 60 * 24),
  );
};

function mergeData(competencia, backlog) {
  // 1. Monta índice do backlog (Chave: Cliente + Data)
  const idx = {};
  backlog.forEach((row) => {
    const rawCliente = row["Cliente"];
    const rawEntrega = row["Data Entrega"];
    // O backlog mantém o campo Cliente para a chave
    const key = `${norm(rawCliente)}||${padronizarMesAno(rawEntrega)}`;

    if (!idx[key]) idx[key] = [];
    idx[key].push(row);
  });

  // 2. Percorre competencia (Chave: PROJETO + Data)
  const result = competencia.map((row) => {
    const rawProjeto = row["Projeto"]; // Mudança: Usando Projeto para o match
    const rawCompetencia = row["Competência"];

    // Cruzamos o PROJETO do controle com o CLIENTE do backlog
    const key = `${norm(rawProjeto)}||${padronizarMesAno(rawCompetencia)}`;
    const matches = idx[key] ?? [];

    const extra = matches[0] ?? {};
    return {
      ...row,
      "Data Faturamento": extra["Data Faturamento (fórmula)"] ?? null,
      "Data Recebimento": extra["Data Recebimento (fórmula)"] ?? null,
      _hasMatch: matches.length > 0,
    };
  });

  return result;
}

// ─── Sub-componentes ──────────────────────────────────────────────────────────

function KpiCard({ icon: Icon, label, value, sub, C, color }) {
  return (
    <div
      style={{
        background: C.card,
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: "16px 20px",
        display: "flex",
        flexDirection: "column",
        gap: 6,
        boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div
          style={{
            background: `${color}22`,
            borderRadius: 8,
            padding: 6,
            display: "flex",
          }}
        >
          <Icon size={16} color={color} />
        </div>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: C.muted,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}
        >
          {label}
        </span>
      </div>
      <span style={{ fontSize: 22, fontWeight: 800, color: C.text }}>
        {value}
      </span>
      {sub && <span style={{ fontSize: 11, color: C.muted }}>{sub}</span>}
    </div>
  );
}

function MultiSelect({ options, placeholder, value, onChange, C }) {
  const [open, setOpen] = useState(false);
  const toggle = (opt) => {
    const next = value.includes(opt)
      ? value.filter((v) => v !== opt)
      : [...value, opt];
    onChange(next);
  };
  const label =
    value.length === 0
      ? placeholder
      : value.length === 1
        ? value[0]
        : `${value.length} selecionados`;

  return (
    <div style={{ position: "relative" }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          border: `1px solid ${C.border}`,
          borderRadius: 7,
          padding: "7px 10px",
          background: C.input,
          cursor: "pointer",
          color: value.length ? C.text : C.muted,
          fontSize: 12,
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </span>
        <ChevronDown size={13} color={C.muted} />
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            zIndex: 100,
            background: C.card,
            border: `1px solid ${C.border}`,
            borderRadius: 8,
            maxHeight: 220,
            overflowY: "auto",
            boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
          }}
        >
          {options.map((opt) => (
            <div
              key={opt}
              onClick={() => toggle(opt)}
              style={{
                padding: "8px 12px",
                cursor: "pointer",
                fontSize: 12,
                color: C.text,
                background: value.includes(opt)
                  ? `${C.brand}22`
                  : "transparent",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: 4,
                  border: `1.5px solid ${value.includes(opt) ? C.brand : C.border}`,
                  background: value.includes(opt) ? C.brand : "transparent",
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {value.includes(opt) && (
                  <span style={{ color: "#fff", fontSize: 9, fontWeight: 900 }}>
                    ✓
                  </span>
                )}
              </span>
              {opt}
            </div>
          ))}
          {options.length === 0 && (
            <div style={{ padding: "8px 12px", fontSize: 12, color: C.muted }}>
              Sem opções
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DateFilter({
  label,
  startValue,
  endValue,
  onChangeStart,
  onChangeEnd,
  C,
}) {
  return (
    <div>
      <label
        style={{
          fontSize: 11,
          color: C.muted,
          display: "block",
          marginBottom: 4,
          fontWeight: 600,
        }}
      >
        {label}
      </label>
      <div style={{ display: "flex", gap: 6 }}>
        {[
          { val: startValue, fn: onChangeStart, ph: "De" },
          { val: endValue, fn: onChangeEnd, ph: "Até" },
        ].map(({ val, fn, ph }) => (
          <input
            key={ph}
            type="date"
            value={val}
            onChange={(e) => fn(e.target.value)}
            title={ph}
            style={{
              flex: 1,
              border: `1px solid ${C.border}`,
              borderRadius: 7,
              padding: "7px 8px",
              background: C.input,
              color: val ? C.text : C.muted,
              fontSize: 11,
              outline: "none",
            }}
          />
        ))}
      </div>
    </div>
  );
}

function SortIcon({ col, sortCol, sortDir }) {
  if (sortCol !== col)
    return <ChevronsUpDown size={12} style={{ opacity: 0.3 }} />;
  return sortDir === "asc" ? (
    <ChevronUp size={12} />
  ) : (
    <ChevronDown size={12} />
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────
export default function ControleCompetencia() {
  const navigate = useNavigate();
  const [isLight, setIsLight] = useState(true);
  const C = THEMES[isLight ? "light" : "dark"];

  const [rawData, setRawData] = useState([]);
  const [dadosNfe, setDadosNfe] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  // Filtros
  const hoje = new Date();
  const hojeFormatado = hoje.toISOString().split("T")[0]; // yyyy-mm-dd

  const [filters, setFilters] = useState({
    cliente: [],
    solicitante: [],
    projeto: [],
    competenciaInicio: "2025-10-01",
    competenciaFim: hojeFormatado,
    apenasComMatch: true,
  });
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 15;

  // Sort
  const [sortCol, setSortCol] = useState("Competência");
  const [sortDir, setSortDir] = useState("asc");

  // ── Fetch e merge ──
  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // Busca os três endpoints ao mesmo tempo
        const [resComp, resBack26, resBack25, dadosFaturamento] =
          await Promise.all([
            getDadosControleCompetencia(),
            getDadosBacklog2026(),
            getDadosBacklog2025(),
            getDadosNfe(),
          ]);
        console.log("dadosFaturamento :>> ", resComp);

        const nfeList = dadosFaturamento?.data ?? [];
        setDadosNfe(nfeList);

        const competencia = (resComp?.data ?? []).filter((row) => {
          const valor =
            parseFloat(
              String(row["Saldo Bruto"] ?? "0")
                .replace(/[^\d.,-]/g, "")
                .replace(",", "."),
            ) || 0;
          return valor !== 0; // Só mantém o que for diferente de zero
        });

        const backlogTotal = [
          ...(resBack26?.data ?? []),
          ...(resBack25?.data ?? []),
        ];

        const merged = mergeData(competencia, backlogTotal);
        setRawData(merged);
        setLastUpdate(new Date().toLocaleTimeString("pt-BR"));
      } catch (e) {
        console.error("Erro no carregamento:", e);
        setError(e.message ?? "Erro ao carregar dados.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ── Opções de filtro ──
  const opts = useMemo(
    () => ({
      cliente: [
        ...new Set(rawData.map((r) => r["Cliente"] ?? "").filter(Boolean)),
      ].sort(),
      solicitante: [
        ...new Set(rawData.map((r) => r["Solicitante"] ?? "").filter(Boolean)),
      ].sort(),
      projeto: [
        ...new Set(rawData.map((r) => r["Projeto"] ?? "").filter(Boolean)),
      ].sort(),
    }),
    [rawData],
  );

  // ── Filtragem e injeção do Aging ──
  const filtrado = useMemo(() => {
    const inRange = (val, start, end) => {
      const d = parseDate(val);
      if (!d) return true;

      // Normaliza para o nível do mês para que não rejeite itens da mesma competência
      // baseando-se estritamente no "dia 1" vs "dia 15".
      const valTime = new Date(d.getFullYear(), d.getMonth(), 1).getTime();

      if (start) {
        const startDate = new Date(start + "T00:00:00");
        const startTime = new Date(
          startDate.getFullYear(),
          startDate.getMonth(),
          1,
        ).getTime();
        if (valTime < startTime) return false;
      }
      if (end) {
        const endDate = new Date(end + "T00:00:00");
        const endTime = new Date(
          endDate.getFullYear(),
          endDate.getMonth(),
          1,
        ).getTime();
        if (valTime > endTime) return false;
      }
      return true;
    };

    const filtrados = rawData.filter((r) => {
      if (filters.cliente.length && !filters.cliente.includes(r["Cliente"]))
        return false;
      if (
        filters.solicitante.length &&
        !filters.solicitante.includes(r["Solicitante"])
      )
        return false;
      if (filters.projeto.length && !filters.projeto.includes(r["Projeto"]))
        return false;
      if (filters.apenasComMatch && !r._hasMatch) return false;
      if (
        !inRange(
          r["Competência"],
          filters.competenciaInicio,
          filters.competenciaFim,
        )
      )
        return false;
      if (search) {
        const q = search.toLowerCase();
        return Object.values(r).some((v) =>
          String(v ?? "")
            .toLowerCase()
            .includes(q),
        );
      }
      return true;
    });

    // Injeta o campo _aging nos dados já filtrados (otimização)
    return filtrados.map((r) => ({
      ...r,
      _aging: calcularAging(r["Competência"]),
    }));
  }, [rawData, filters, search]);

  const totalClientesUnicos = useMemo(() => {
    return new Set(filtrado.map((r) => r["Cliente"])).size;
  }, [filtrado]);

  // ── Ordenação ──
  const sorted = useMemo(() => {
    return [...filtrado].sort((a, b) => {
      let av = a[sortCol];
      let bv = b[sortCol];

      // Se for coluna de data ou competência, ordenar por objeto Date
      if (sortCol === "Competência" || sortCol.includes("Data")) {
        const dateA = parseDate(av)?.getTime() || 0;
        const dateB = parseDate(bv)?.getTime() || 0;
        return sortDir === "asc" ? dateA - dateB : dateB - dateA;
      }

      // Ordenação Numérica para Aging
      if (sortCol === "_aging") {
        const numA = av !== null && av !== undefined ? av : -Infinity;
        const numB = bv !== null && bv !== undefined ? bv : -Infinity;
        return sortDir === "asc" ? numA - numB : numB - numA;
      }

      const cmp = String(av ?? "").localeCompare(String(bv ?? ""), "pt-BR", {
        numeric: true,
      });
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [filtrado, sortCol, sortDir]);

  // ── Paginação ──
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const pageData = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSort = (col) => {
    if (sortCol === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortCol(col);
      setSortDir("asc");
    }
  };

  // ── KPIs ──
  const totalSaldo = useMemo(
    () =>
      filtrado.reduce((acc, r) => {
        const v =
          parseFloat(
            String(r["Saldo Bruto"] ?? "0")
              .replace(/[^\d.,-]/g, "")
              .replace(",", "."),
          ) || 0;
        return acc + v;
      }, 0),
    [filtrado],
  );

  const mediaAging = useMemo(() => {
    if (!filtrado.length) return 0;
    let sum = 0;
    let count = 0;
    filtrado.forEach((r) => {
      if (r._aging !== null && r._aging !== undefined) {
        sum += r._aging;
        count++;
      }
    });
    return count > 0 ? Math.round(sum / count) : 0;
  }, [filtrado]);

  // ── Preparação de Dados para os Gráficos (Recharts) ──
  const chartData = useMemo(() => {
    const parseVal = (v) =>
      parseFloat(
        String(v ?? "0")
          .replace(/[^\d.,-]/g, "")
          .replace(",", "."),
      ) || 0;

    const mapProjeto = {};
    const mapSolicitante = {};
    const mapCompetencia = {};

    filtrado.forEach((row) => {
      const valor = parseVal(row["Saldo Bruto"]);
      const proj = row["Projeto"] || "Não informado";
      const sol = row["Solicitante"] || "Não informado";
      const comp = row["Competência"] || "Não informado";

      mapProjeto[proj] = (mapProjeto[proj] || 0) + valor;
      mapSolicitante[sol] = (mapSolicitante[sol] || 0) + valor;
      mapCompetencia[comp] = (mapCompetencia[comp] || 0) + valor;
    });

    // Ordena do maior para o menor e pega os Top 10 para barras
    const projeto = Object.keys(mapProjeto)
      .map((k) => ({ name: k, value: mapProjeto[k] }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);

    const solicitante = Object.keys(mapSolicitante)
      .map((k) => ({ name: k, value: mapSolicitante[k] }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);

    // Ordena cronologicamente pela Competência (Assumindo formato MM/YYYY)
    const competencia = Object.keys(mapCompetencia)
      .map((k) => {
        const [m, y] = k.split("/");
        return { name: k, value: mapCompetencia[k], _sort: y ? `${y}${m}` : k };
      })
      .sort((a, b) => a._sort.localeCompare(b._sort));

    return { projeto, solicitante, competencia };
  }, [filtrado]);

  // ── Gráfico Comparativo: Saldo Bruto (Competência) vs Faturamento NF-e ──
  const chartComparativo = useMemo(() => {
    const parseVal = (v) =>
      parseFloat(
        String(v ?? "0")
          .replace(/[^\d.,-]/g, "")
          .replace(",", "."),
      ) || 0;

    // Mapeia Saldo Bruto da competência por MM/YYYY (este já vem do estado 'filtrado')
    const mapSaldo = {};
    filtrado.forEach((row) => {
      const comp = padronizarMesAno(row["Competência"]);
      if (!comp) return;
      mapSaldo[comp] = (mapSaldo[comp] || 0) + parseVal(row["Saldo Bruto"]);
    });

    // Mapeia Valor Bruto da NF-e por MM/YYYY aplicando o filtro de datas
    const mapNfe = {};
    dadosNfe.forEach((item) => {
      const mes = String(item["Mês DRE"] ?? "").padStart(2, "0");
      const ano = String(item["Ano DRE"] ?? "");
      if (!mes || !ano) return;

      // Aplicando o mesmo filtro de Competência na base NFe
      const itemTime = new Date(parseInt(ano), parseInt(mes) - 1, 1).getTime();

      if (filters.competenciaInicio) {
        const startDate = new Date(filters.competenciaInicio + "T00:00:00");
        const startTime = new Date(
          startDate.getFullYear(),
          startDate.getMonth(),
          1,
        ).getTime();
        if (itemTime < startTime) return;
      }
      if (filters.competenciaFim) {
        const endDate = new Date(filters.competenciaFim + "T00:00:00");
        const endTime = new Date(
          endDate.getFullYear(),
          endDate.getMonth(),
          1,
        ).getTime();
        if (itemTime > endTime) return;
      }

      const chave = `${mes}/${ano}`;
      mapNfe[chave] = (mapNfe[chave] || 0) + parseVal(item["Valor Bruto"]);
    });

    // União de todas as chaves (dos dois mapas) e ordenação cronológica
    const todasChaves = [
      ...new Set([...Object.keys(mapSaldo), ...Object.keys(mapNfe)]),
    ];
    return todasChaves
      .map((k) => {
        const [m, y] = k.split("/");
        return {
          name: k,
          saldoBruto: mapSaldo[k] ?? 0,
          faturamento: mapNfe[k] ?? 0,
          _sort: y ? `${y}${m}` : k,
        };
      })
      .sort((a, b) => a._sort.localeCompare(b._sort));
  }, [filtrado, dadosNfe, filters.competenciaInicio, filters.competenciaFim]);

  // ── Tooltips ──
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            background: C.card,
            border: `1px solid ${C.border}`,
            padding: "8px 12px",
            borderRadius: 8,
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: 12,
              fontWeight: 700,
              color: C.text,
              marginBottom: 4,
            }}
          >
            {label}
          </p>
          <p style={{ margin: 0, fontSize: 12, color: C.brand }}>
            Valor: {fmtBRL(payload[0].value)}
          </p>
        </div>
      );
    }
    return null;
  };

  const CustomTooltipComparativo = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            background: C.card,
            border: `1px solid ${C.border}`,
            padding: "10px 14px",
            borderRadius: 8,
          }}
        >
          <p
            style={{
              margin: "0 0 6px",
              fontSize: 12,
              fontWeight: 700,
              color: C.text,
            }}
          >
            {label}
          </p>
          {payload.map((p) => (
            <p
              key={p.dataKey}
              style={{ margin: "2px 0", fontSize: 12, color: p.color }}
            >
              {p.name}: {fmtBRL(p.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const activeFilters = [
    filters.cliente.length,
    filters.solicitante.length,
    filters.projeto.length,
    filters.competenciaInicio || filters.competenciaFim ? 1 : 0,
    filters.apenasComMatch ? 1 : 0,
  ].reduce((a, b) => a + (b ? 1 : 0), 0);

  const limpar = () => {
    setFilters({
      cliente: [],
      solicitante: [],
      projeto: [],
      competenciaInicio: "",
      competenciaFim: "",
      apenasComMatch: false,
    });
    setSearch("");
    setPage(1);
  };

  // ── Colunas da tabela (Adicionado _aging na penúltima posição) ──
  const COLS = [
    { key: "Cliente", label: "Cliente", width: "14%" },
    { key: "Solicitante", label: "Solicitante", width: "12%" },
    { key: "Projeto", label: "Projeto", width: "16%" },
    { key: "Competência", label: "Competência", width: "10%" },
    { key: "Data Faturamento", label: "Dt. Faturamento", width: "11%" },
    { key: "Data Recebimento", label: "Dt. Recebimento", width: "11%" },
    { key: "_aging", label: "Aging", width: "9%", align: "center" }, // NOVA COLUNA
    { key: "Saldo Bruto", label: "Saldo Bruto", width: "14%", align: "right" },
  ];
  const location = useLocation();
  const isDashRoute = location.pathname.includes("-dash");

  // ── Render ──
  return (
    <div
      style={{
        display: "flex",
        height: "100vh",
        overflow: "hidden",
        background: C.bg,
        fontFamily: "'DM Sans', 'Inter', system-ui, sans-serif",
        color: C.text,
      }}
    >
      {isDashRoute ? (
        <SideBarNavDash isLight={isLight} />
      ) : (
        <SideBarNavFaturamento isLight={isLight} />
      )}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
        }}
      >
        {/* Header */}
        <header
          style={{
            background: "linear-gradient(90deg, #4c1d95 0%, #9d174d 100%)",
            color: "#fff",
            padding: "14px 24px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 22, fontWeight: 800 }}>YOUX</span>
            <span style={{ fontSize: 18, fontWeight: 300 }}>GROUP</span>
            <span style={{ marginLeft: 12, fontSize: 13, opacity: 0.7 }}>
              / Controle de Competência
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {lastUpdate && (
              <span style={{ fontSize: 11, opacity: 0.6 }}>
                Atualizado às {lastUpdate}
              </span>
            )}
            <button
              onClick={() => setIsLight((l) => !l)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                background: "rgba(255,255,255,0.12)",
                border: "1px solid rgba(255,255,255,0.25)",
                borderRadius: 8,
                padding: "7px 12px",
                cursor: "pointer",
                color: "#fff",
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              {isLight ? <Sun size={14} /> : <Moon size={14} />}{" "}
              {isLight ? "Tema Claro" : "Tema Escuro"}
            </button>
            <button
              onClick={() => navigate("/inicio")}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                background: "rgba(255,255,255,0.12)",
                border: "1px solid rgba(255,255,255,0.25)",
                borderRadius: 8,
                padding: "7px 14px",
                cursor: "pointer",
                color: "#fff",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              Menu Principal
            </button>
          </div>
        </header>

        <main
          style={{
            padding: "24px 28px",
            maxWidth: 1500,
            margin: "0 auto",
            width: "100%",
          }}
        >
          {/* KPIs */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3,1fr)",
              gap: 14,
              marginBottom: 20,
            }}
          >
            <KpiCard
              icon={FileText}
              label="Total de Clientes"
              value={totalClientesUnicos}
              C={C}
              color="#7C3AED"
            />
            <KpiCard
              icon={Clock}
              label="Média de Aging"
              value={`${mediaAging} dias`}
              C={C}
              color="#D97706"
              sub="Ref. Hoje - Último dia da Competência"
            />
            <KpiCard
              icon={DollarSign}
              label="Competência Total"
              value={fmtBRL(totalSaldo)}
              C={C}
              color="#059669"
              sub={`${sorted.length} registros filtrados`}
            />
          </div>

          {/* Filtros */}
          <div
            style={{
              background: C.card,
              border: `1px solid ${C.border}`,
              borderRadius: 10,
              padding: 16,
              marginBottom: 20,
              boxShadow: isLight
                ? "0 1px 4px rgba(0,0,0,0.06)"
                : "0 1px 4px rgba(0,0,0,0.3)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 12,
              }}
            >
              <Filter size={14} color={C.muted} />
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: C.muted,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                Filtros
              </span>
              {activeFilters > 0 && (
                <span
                  style={{
                    background: C.brand,
                    color: "#fff",
                    fontSize: 10,
                    fontWeight: 700,
                    borderRadius: 20,
                    padding: "1px 7px",
                  }}
                >
                  {activeFilters} ativo(s)
                </span>
              )}
            </div>

            {/* Linha 1: selects */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4,1fr)",
                gap: 12,
                marginBottom: 12,
              }}
            >
              <div>
                <label
                  style={{
                    fontSize: 11,
                    color: C.muted,
                    display: "block",
                    marginBottom: 4,
                    fontWeight: 600,
                  }}
                >
                  Cliente
                </label>
                <MultiSelect
                  options={opts.cliente}
                  placeholder="Cliente"
                  value={filters.cliente}
                  C={C}
                  onChange={(v) => {
                    setFilters((f) => ({ ...f, cliente: v }));
                    setPage(1);
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    fontSize: 11,
                    color: C.muted,
                    display: "block",
                    marginBottom: 4,
                    fontWeight: 600,
                  }}
                >
                  Solicitante
                </label>
                <MultiSelect
                  options={opts.solicitante}
                  placeholder="Solicitante"
                  value={filters.solicitante}
                  C={C}
                  onChange={(v) => {
                    setFilters((f) => ({ ...f, solicitante: v }));
                    setPage(1);
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    fontSize: 11,
                    color: C.muted,
                    display: "block",
                    marginBottom: 4,
                    fontWeight: 600,
                  }}
                >
                  Projeto
                </label>
                <MultiSelect
                  options={opts.projeto}
                  placeholder="Projeto"
                  value={filters.projeto}
                  C={C}
                  onChange={(v) => {
                    setFilters((f) => ({ ...f, projeto: v }));
                    setPage(1);
                  }}
                />
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3,1fr)",
                  gap: 12,
                  marginBottom: 12,
                }}
              >
                <DateFilter
                  label="Competência"
                  startValue={filters.competenciaInicio}
                  endValue={filters.competenciaFim}
                  onChangeStart={(v) =>
                    setFilters((f) => ({ ...f, competenciaInicio: v }))
                  }
                  onChangeEnd={(v) =>
                    setFilters((f) => ({ ...f, competenciaFim: v }))
                  }
                  C={C}
                />
              </div>
            </div>

            {/* Linha 3: busca + toggle */}
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  border: `1px solid ${C.border}`,
                  borderRadius: 7,
                  padding: "7px 10px",
                }}
              >
                <Search size={13} color={C.muted} />
                <input
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Busca livre em todos os campos…"
                  style={{
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    fontSize: 12,
                    color: C.text,
                    width: "100%",
                  }}
                />
                {search && (
                  <button
                    onClick={() => setSearch("")}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: 0,
                    }}
                  >
                    <X size={13} color={C.muted} />
                  </button>
                )}
              </div>

              {(activeFilters > 0 || search) && (
                <button
                  onClick={limpar}
                  style={{
                    fontSize: 13,
                    color: C.brand,
                    fontWeight: 700,
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 0,
                    whiteSpace: "nowrap",
                  }}
                >
                  Limpar Filtros
                </button>
              )}
            </div>
          </div>

          {/* ── Gráficos ── */}
          {!loading && !error && filtrado.length > 0 && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: 16,
                marginBottom: 20,
              }}
            >
              {/* Gráfico 1: Projeto x Saldo Bruto */}
              <div
                style={{
                  background: C.card,
                  border: `1px solid ${C.border}`,
                  borderRadius: 10,
                  padding: 16,
                }}
              >
                <h3
                  style={{
                    fontSize: 13,
                    color: C.muted,
                    textTransform: "uppercase",
                    marginBottom: 16,
                    marginTop: 0,
                  }}
                >
                  Projetos (Competência)
                </h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart
                    data={chartData.projeto}
                    layout="vertical"
                    margin={{ left: 10, right: 20 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={C.border}
                      horizontal={true}
                      vertical={false}
                    />
                    <XAxis
                      type="number"
                      stroke={C.muted}
                      fontSize={10}
                      tickFormatter={(val) => `R$ ${(val / 1000).toFixed(0)}k`}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      stroke={C.muted}
                      fontSize={10}
                      width={100}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="value" fill="#7C3AED" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Gráfico 2: Solicitante x Saldo Bruto */}
              <div
                style={{
                  background: C.card,
                  border: `1px solid ${C.border}`,
                  borderRadius: 10,
                  padding: 16,
                }}
              >
                <h3
                  style={{
                    fontSize: 13,
                    color: C.muted,
                    textTransform: "uppercase",
                    marginBottom: 16,
                    marginTop: 0,
                  }}
                >
                  Solicitantes (Competência)
                </h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart
                    data={chartData.solicitante}
                    layout="vertical"
                    margin={{ left: 10, right: 20 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={C.border}
                      horizontal={true}
                      vertical={false}
                    />
                    <XAxis
                      type="number"
                      stroke={C.muted}
                      fontSize={10}
                      tickFormatter={(val) => `R$ ${(val / 1000).toFixed(0)}k`}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      stroke={C.muted}
                      fontSize={10}
                      width={100}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="value" fill="#059669" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Gráfico 4: Comparativo Saldo Bruto vs Faturamento NF-e — Barras Empilhadas */}
          <div
            style={{
              background: C.card,
              border: `1px solid ${C.border}`,
              borderRadius: 10,
              padding: 16,
              marginBottom: 20,
            }}
          >
            <h3
              style={{
                fontSize: 13,
                color: C.muted,
                textTransform: "uppercase",
                marginBottom: 12,
                marginTop: 0,
              }}
            >
              Comparativo — Competência vs Faturamento
            </h3>

            {/* Legenda */}
            <div style={{ display: "flex", gap: 20, marginBottom: 12 }}>
              {[
                { color: "#E11D48", label: "Saldo Bruto (Competência)" },
                { color: "#2563EB", label: "Faturamento" },
              ].map(({ color, label }) => (
                <div
                  key={label}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: 11,
                    color: C.muted,
                  }}
                >
                  <span
                    style={{
                      width: 12,
                      height: 12,
                      background: color,
                      borderRadius: 3,
                      display: "inline-block",
                    }}
                  />
                  {label}
                </div>
              ))}
            </div>

            <ResponsiveContainer width="100%" height={280}>
              <BarChart
                data={chartComparativo}
                margin={{ top: 10, right: 24, left: 10, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={C.border}
                  vertical={false}
                />
                <XAxis dataKey="name" stroke={C.muted} fontSize={10} />
                <YAxis
                  stroke={C.muted}
                  fontSize={10}
                  tickFormatter={(val) => `R$ ${(val / 1000).toFixed(0)}k`}
                />
                <Tooltip content={<CustomTooltipComparativo />} />
                <Bar
                  dataKey="saldoBruto"
                  name="Competência"
                  stackId="comparativo"
                  fill="#E11D48"
                  radius={[0, 0, 0, 0]}
                />
                <Bar
                  dataKey="faturamento"
                  name="Faturamento"
                  stackId="comparativo"
                  fill="#2563EB"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Tabela */}
          <div
            style={{
              background: C.card,
              border: `1px solid ${C.border}`,
              borderRadius: 10,
              overflow: "hidden",
              boxShadow: isLight
                ? "0 1px 4px rgba(0,0,0,0.06)"
                : "0 1px 4px rgba(0,0,0,0.3)",
            }}
          >
            {loading && (
              <div
                style={{
                  padding: 40,
                  textAlign: "center",
                  color: C.muted,
                  fontSize: 13,
                }}
              >
                Carregando dados…
              </div>
            )}

            {error && (
              <div
                style={{
                  padding: 24,
                  color: "#EF4444",
                  fontSize: 13,
                  textAlign: "center",
                }}
              >
                {error}
              </div>
            )}

            {!loading && !error && (
              <>
                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: 12,
                    }}
                  >
                    <thead>
                      <tr
                        style={{
                          background: isLight ? "#f0f0f8" : "#14141c",
                          borderBottom: `1px solid ${C.border}`,
                        }}
                      >
                        {COLS.map((col) => (
                          <th
                            key={col.key}
                            onClick={() =>
                              col.key !== "_hasMatch" && handleSort(col.key)
                            }
                            style={{
                              padding: "10px 12px",
                              textAlign: col.align ?? "left",
                              color: C.muted,
                              fontWeight: 700,
                              fontSize: 11,
                              textTransform: "uppercase",
                              letterSpacing: "0.05em",
                              cursor:
                                col.key !== "_hasMatch" ? "pointer" : "default",
                              whiteSpace: "nowrap",
                              userSelect: "none",
                              width: col.width,
                            }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              {col.label}
                              {col.key !== "_hasMatch" && (
                                <SortIcon
                                  col={col.key}
                                  sortCol={sortCol}
                                  sortDir={sortDir}
                                />
                              )}
                            </span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {pageData.length === 0 && (
                        <tr>
                          <td
                            colSpan={COLS.length}
                            style={{
                              padding: 32,
                              textAlign: "center",
                              color: C.muted,
                              fontSize: 13,
                            }}
                          >
                            Nenhum registro encontrado.
                          </td>
                        </tr>
                      )}
                      {pageData.map((row, i) => (
                        <tr
                          key={i}
                          style={{
                            borderBottom: `1px solid ${C.border}`,
                            background:
                              i % 2 === 0
                                ? "transparent"
                                : isLight
                                  ? "#f9f9fc"
                                  : "#16161f",
                            transition: "background 0.15s",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background = C.hover)
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.background =
                              i % 2 === 0
                                ? "transparent"
                                : isLight
                                  ? "#f9f9fc"
                                  : "#16161f")
                          }
                        >
                          <td
                            style={{
                              padding: "9px 12px",
                              color: C.text,
                              fontWeight: 600,
                            }}
                          >
                            {row["Cliente"] ?? "—"}
                          </td>
                          <td style={{ padding: "9px 12px", color: C.muted }}>
                            {row["Solicitante"] ?? "—"}
                          </td>
                          <td
                            style={{
                              padding: "9px 12px",
                              color: C.text,
                              maxWidth: 220,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={row["Projeto"]}
                          >
                            {row["Projeto"] ?? "—"}
                          </td>
                          <td style={{ padding: "9px 12px", color: C.muted }}>
                            {row["Competência"] ?? "—"}
                          </td>
                          <td
                            style={{
                              padding: "9px 12px",
                              color: row["Data Faturamento"] ? C.text : C.muted,
                            }}
                          >
                            {fmtDate(row["Data Faturamento"])}
                          </td>
                          <td
                            style={{
                              padding: "9px 12px",
                              color: row["Data Recebimento"] ? C.text : C.muted,
                            }}
                          >
                            {fmtDate(row["Data Recebimento"])}
                          </td>
                          <td
                            style={{
                              padding: "9px 12px",
                              color: C.text,
                              textAlign: "center",
                              fontWeight: 600,
                            }}
                          >
                            {row._aging !== null && row._aging !== undefined
                              ? `${row._aging}d`
                              : "—"}
                          </td>
                          <td
                            style={{
                              padding: "9px 12px",
                              color: "#059669",
                              fontWeight: 700,
                              textAlign: "right",
                            }}
                          >
                            {fmtBRL(row["Saldo Bruto"])}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Paginação */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "12px 16px",
                    borderTop: `1px solid ${C.border}`,
                  }}
                >
                  <span style={{ fontSize: 12, color: C.muted }}>
                    {filtrado.length} registro(s) · página {page} de{" "}
                    {totalPages}
                  </span>
                  <div style={{ display: "flex", gap: 6 }}>
                    {[
                      {
                        label: "«",
                        fn: () => setPage(1),
                        disabled: page === 1,
                      },
                      {
                        label: "‹",
                        fn: () => setPage((p) => Math.max(1, p - 1)),
                        disabled: page === 1,
                      },
                      {
                        label: "›",
                        fn: () => setPage((p) => Math.min(totalPages, p + 1)),
                        disabled: page === totalPages,
                      },
                      {
                        label: "»",
                        fn: () => setPage(totalPages),
                        disabled: page === totalPages,
                      },
                    ].map(({ label, fn, disabled }) => (
                      <button
                        key={label}
                        onClick={fn}
                        disabled={disabled}
                        style={{
                          padding: "4px 10px",
                          borderRadius: 6,
                          border: `1px solid ${C.border}`,
                          background: disabled ? "transparent" : C.card,
                          color: disabled ? C.muted : C.text,
                          cursor: disabled ? "not-allowed" : "pointer",
                          fontSize: 13,
                          fontWeight: 600,
                        }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
